import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Toast from "@/components/Toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ClassView({ user, onLogout }) {
  const { classId } = useParams();
  const navigate = useNavigate();
  const [classData, setClassData] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAnswersModal, setShowAnswersModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentAnswers, setStudentAnswers] = useState([]);
  const [showModulesModal, setShowModulesModal] = useState(false);
  const [modules, setModules] = useState([]);
  const [classProgress, setClassProgress] = useState([]);
  const [savingModuleId, setSavingModuleId] = useState(null);
  const [toast, setToast] = useState(null);
  
  // New state for announcements and due dates
  const [announcements, setAnnouncements] = useState([]);
  const [dueDates, setDueDates] = useState([]);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [showDueDatesModal, setShowDueDatesModal] = useState(false);
  const [announcementForm, setAnnouncementForm] = useState({ title: "", content: "" });
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [savingAnnouncement, setSavingAnnouncement] = useState(false);
  const [savingDueDate, setSavingDueDate] = useState(null);

  useEffect(() => {
    loadClassDetails();
  }, [classId]);

  const loadClassDetails = async () => {
    try {
      const requests = [
        axios.get(`${API}/classes/${classId}`),
        axios.get(`${API}/classes/${classId}/announcements`),
        axios.get(`${API}/classes/${classId}/due-dates`)
      ];
      
      if (user.role === "educator") {
        requests.push(axios.get(`${API}/classes/${classId}/progress`));
      }
      
      const [classRes, announcementsRes, dueDatesRes, progressRes] = await Promise.all(requests);
      
      setClassData(classRes.data.class);
      setStudents(classRes.data.students || []);
      setAnnouncements(announcementsRes.data || []);
      setDueDates(dueDatesRes.data || []);
      if (progressRes) {
        setClassProgress(progressRes.data || []);
      }
    } catch (err) {
      console.error("Error loading class:", err);
    } finally {
      setLoading(false);
    }
  };

  const viewStudentAnswers = async (studentId, studentName) => {
    try {
      const response = await axios.get(`${API}/answers/student/${studentId}`);
      setStudentAnswers(response.data);
      setSelectedStudent({ id: studentId, name: studentName });
      setShowAnswersModal(true);
    } catch (err) {
      console.error("Error loading student answers:", err);
      alert("Failed to load student answers");
    }
  };

  const loadModules = async () => {
    try {
      const response = await axios.get(`${API}/classes/${classId}/modules`);
      setModules(response.data);
    } catch (err) {
      console.error("Error loading modules:", err);
    }
  };

  const handleToggleModule = async (moduleId, currentlyAssigned) => {
    setSavingModuleId(moduleId);
    try {
      if (currentlyAssigned) {
        await axios.delete(`${API}/classes/${classId}/modules/${moduleId}/assign`);
      } else {
        await axios.post(`${API}/classes/${classId}/modules/${moduleId}/assign`);
      }
      await loadModules();
      setToast({ message: "Module updated successfully!", type: "success" });
    } catch (err) {
      console.error("Error toggling module:", err);
      setToast({ message: "Failed to update module. Please try again.", type: "error" });
    } finally {
      setSavingModuleId(null);
    }
  };

  const openModulesModal = async () => {
    await loadModules();
    setShowModulesModal(true);
  };

  // Announcement handlers
  const handleCreateAnnouncement = async (e) => {
    e.preventDefault();
    setSavingAnnouncement(true);
    try {
      if (editingAnnouncement) {
        await axios.put(`${API}/classes/${classId}/announcements/${editingAnnouncement.id}`, announcementForm);
        setToast({ message: "Announcement updated!", type: "success" });
      } else {
        await axios.post(`${API}/classes/${classId}/announcements`, announcementForm);
        setToast({ message: "Announcement posted!", type: "success" });
      }
      setAnnouncementForm({ title: "", content: "" });
      setEditingAnnouncement(null);
      setShowAnnouncementModal(false);
      // Reload announcements
      const res = await axios.get(`${API}/classes/${classId}/announcements`);
      setAnnouncements(res.data || []);
    } catch (err) {
      console.error("Error saving announcement:", err);
      setToast({ message: "Failed to save announcement", type: "error" });
    } finally {
      setSavingAnnouncement(false);
    }
  };

  const handleEditAnnouncement = (announcement) => {
    setEditingAnnouncement(announcement);
    setAnnouncementForm({ title: announcement.title, content: announcement.content });
    setShowAnnouncementModal(true);
  };

  const handleDeleteAnnouncement = async (announcementId) => {
    if (!window.confirm("Are you sure you want to delete this announcement?")) return;
    try {
      await axios.delete(`${API}/classes/${classId}/announcements/${announcementId}`);
      setAnnouncements(announcements.filter(a => a.id !== announcementId));
      setToast({ message: "Announcement deleted", type: "success" });
    } catch (err) {
      console.error("Error deleting announcement:", err);
      setToast({ message: "Failed to delete announcement", type: "error" });
    }
  };

  // Due date handlers
  const handleSetDueDate = async (sessionNumber, dateValue) => {
    if (!dateValue) return;
    setSavingDueDate(sessionNumber);
    try {
      await axios.post(`${API}/classes/${classId}/due-dates`, {
        session_number: sessionNumber,
        due_date: new Date(dateValue).toISOString()
      });
      const res = await axios.get(`${API}/classes/${classId}/due-dates`);
      setDueDates(res.data || []);
      setToast({ message: `Due date set for Session ${sessionNumber}`, type: "success" });
    } catch (err) {
      console.error("Error setting due date:", err);
      setToast({ message: "Failed to set due date", type: "error" });
    } finally {
      setSavingDueDate(null);
    }
  };

  const handleRemoveDueDate = async (sessionNumber) => {
    setSavingDueDate(sessionNumber);
    try {
      await axios.delete(`${API}/classes/${classId}/due-dates/${sessionNumber}`);
      setDueDates(dueDates.filter(d => d.session_number !== sessionNumber));
      setToast({ message: `Due date removed for Session ${sessionNumber}`, type: "success" });
    } catch (err) {
      console.error("Error removing due date:", err);
      setToast({ message: "Failed to remove due date", type: "error" });
    } finally {
      setSavingDueDate(null);
    }
  };

  const getDueDateForSession = (sessionNumber) => {
    const dd = dueDates.find(d => d.session_number === sessionNumber);
    return dd ? new Date(dd.due_date) : null;
  };

  const isPastDue = (sessionNumber) => {
    const dd = getDueDateForSession(sessionNumber);
    return dd && dd < new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!classData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="container mx-auto px-4 py-8 text-center">
          <p className="text-xl text-gray-600">Class not found</p>
          <button onClick={() => navigate("/dashboard")} className="educator-button px-6 py-3 rounded-lg text-white font-semibold mt-4">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      <Navbar user={user} onLogout={onLogout} />

      <div className="container mx-auto px-4 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate("/dashboard")}
          className="mb-6 text-gray-600 hover:text-gray-800 font-semibold flex items-center gap-2"
          data-testid="back-to-dashboard-btn"
        >
          ← Back to Dashboard
        </button>

        {/* Class Header */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-800 mb-2" data-testid="class-name">{classData.name}</h1>
              <p className="text-gray-600 mb-4">{classData.description || "No description"}</p>
              
              {user.role === "educator" && (
                <div className="flex flex-wrap gap-3">
                  <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4 inline-block mb-4">
                    <div className="text-sm text-gray-600 mb-1">Join Code (share with students)</div>
                    <div className="text-3xl font-bold text-yellow-700 tracking-wider" data-testid="class-join-code">
                      {classData.join_code}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={openModulesModal}
                      className="educator-button px-6 py-2 rounded-lg text-white font-semibold"
                      data-testid="manage-modules-btn"
                    >
                      📚 Manage Modules
                    </button>
                    <button
                      onClick={() => navigate(`/classes/${classId}/assessments`)}
                      className="bg-purple-500 hover:bg-purple-600 px-6 py-2 rounded-lg text-white font-semibold transition-colors"
                      data-testid="view-assessments-btn"
                    >
                      📊 Assessment Dashboard
                    </button>
                    <button
                      onClick={() => setShowDueDatesModal(true)}
                      className="bg-orange-500 hover:bg-orange-600 px-6 py-2 rounded-lg text-white font-semibold transition-colors"
                      data-testid="manage-due-dates-btn"
                    >
                      📅 Set Due Dates
                    </button>
                    <button
                      onClick={() => {
                        setEditingAnnouncement(null);
                        setAnnouncementForm({ title: "", content: "" });
                        setShowAnnouncementModal(true);
                      }}
                      className="bg-blue-500 hover:bg-blue-600 px-6 py-2 rounded-lg text-white font-semibold transition-colors"
                      data-testid="create-announcement-btn"
                    >
                      📢 Post Announcement
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="text-center">
              <div className="text-5xl font-bold text-green-600">{students.length}</div>
              <div className="text-gray-600">Students</div>
            </div>
          </div>
        </div>

        {/* Announcements Section */}
        {announcements.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8" data-testid="announcements-section">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
              <span>📢</span> Class Announcements
            </h2>
            <div className="space-y-4">
              {announcements.map((announcement) => (
                <div
                  key={announcement.id}
                  className="border-2 border-blue-100 bg-blue-50 rounded-xl p-6"
                  data-testid={`announcement-${announcement.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-800 mb-2">{announcement.title}</h3>
                      <p className="text-gray-700 whitespace-pre-wrap">{announcement.content}</p>
                      <div className="mt-3 text-sm text-gray-500">
                        Posted by {announcement.created_by_name} • {new Date(announcement.created_at).toLocaleDateString()}
                        {announcement.updated_at && (
                          <span className="ml-2">(edited)</span>
                        )}
                      </div>
                    </div>
                    {user.role === "educator" && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditAnnouncement(announcement)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-semibold"
                          data-testid={`edit-announcement-${announcement.id}`}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteAnnouncement(announcement.id)}
                          className="text-red-600 hover:text-red-800 text-sm font-semibold"
                          data-testid={`delete-announcement-${announcement.id}`}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Summary Metrics (Educator Only) */}
        {user.role === "educator" && classProgress.length > 0 && (
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="text-gray-600 text-sm font-semibold mb-2">Total Students</div>
              <div className="text-4xl font-bold text-green-600">{students.length}</div>
            </div>
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="text-gray-600 text-sm font-semibold mb-2">Total Lessons Completed</div>
              <div className="text-4xl font-bold text-blue-600">
                {classProgress.reduce((sum, s) => sum + s.lessons_completed, 0)}
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="text-gray-600 text-sm font-semibold mb-2">Total Answers Submitted</div>
              <div className="text-4xl font-bold text-purple-600">
                {classProgress.reduce((sum, s) => sum + s.answers_submitted, 0)}
              </div>
            </div>
          </div>
        )}

        {/* Students List */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            {user.role === "educator" ? "Student Progress & Roster" : "Students"}
          </h2>
          
          {students.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">👥</div>
              <p className="text-gray-600">No students have joined yet</p>
              {user.role === "educator" && (
                <p className="text-sm text-gray-500 mt-2">Share the join code with your students!</p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Student Name</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Email</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Lessons Completed</th>
                    {user.role === "educator" && (
                      <>
                        <th className="text-center py-3 px-4 font-semibold text-gray-700">Answers Submitted</th>
                        <th className="text-center py-3 px-4 font-semibold text-gray-700">Last Activity</th>
                        <th className="text-center py-3 px-4 font-semibold text-gray-700">Actions</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {students.map((student) => {
                    const studentProgress = classProgress.find(p => p.student_id === student.id);
                    return (
                      <tr key={student.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`student-row-${student.id}`}>
                        <td className="py-4 px-4 font-medium text-gray-800">{student.name}</td>
                        <td className="py-4 px-4 text-gray-600">{student.email}</td>
                        <td className="py-4 px-4 text-center">
                          <span className="inline-block bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                            {studentProgress?.lessons_completed || student.completed_lessons || 0}
                          </span>
                        </td>
                        {user.role === "educator" && (
                          <>
                            <td className="py-4 px-4 text-center">
                              <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                                {studentProgress?.answers_submitted || 0}
                              </span>
                            </td>
                            <td className="py-4 px-4 text-center text-sm text-gray-600">
                              {studentProgress?.last_activity 
                                ? new Date(studentProgress.last_activity).toLocaleDateString()
                                : "No activity yet"}
                            </td>
                            <td className="py-4 px-4 text-center">
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  onClick={() => viewStudentAnswers(student.id, student.name)}
                                  className="text-blue-600 hover:text-blue-800 font-semibold text-sm"
                                  data-testid={`view-answers-${student.id}`}
                                >
                                  Answers
                                </button>
                                <span className="text-gray-300">|</span>
                                <button
                                  onClick={() => navigate(`/workbook/${student.id}`)}
                                  className="text-green-600 hover:text-green-800 font-semibold text-sm"
                                  data-testid={`view-workbook-${student.id}`}
                                >
                                  Workbook
                                </button>
                                <span className="text-gray-300">|</span>
                                <button
                                  onClick={() => navigate(`/ai-business-plan/${student.id}`)}
                                  className="text-orange-600 hover:text-orange-800 font-semibold text-sm"
                                  data-testid={`view-plan-${student.id}`}
                                >
                                  Plan
                                </button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Toast Notification */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Module Management Modal */}
      {showModulesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowModulesModal(false)}>
          <div className="bg-white rounded-2xl p-8 max-w-3xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Manage Class Modules</h2>
              <button
                onClick={() => setShowModulesModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
                data-testid="close-modules-modal"
              >
                ×
              </button>
            </div>

            <p className="text-gray-600 mb-6">
              Enable or disable modules for this class. Students will only see enabled modules.
            </p>

            <div className="space-y-3">
              {modules.map((module) => (
                <div
                  key={module.id}
                  className="flex items-center justify-between p-4 border-2 rounded-lg hover:bg-gray-50"
                  data-testid={`module-item-${module.id}`}
                >
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-800">{module.title}</h3>
                    <p className="text-sm text-gray-600">{module.description}</p>
                  </div>
                  <button
                    onClick={() => handleToggleModule(module.id, module.assigned)}
                    disabled={savingModuleId === module.id}
                    className={`px-6 py-2 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      module.assigned
                        ? "bg-green-500 text-white hover:bg-green-600"
                        : "bg-gray-300 text-gray-700 hover:bg-gray-400"
                    }`}
                    data-testid={`toggle-module-${module.id}`}
                  >
                    {savingModuleId === module.id ? (
                      <>
                        <span className="inline-block animate-spin mr-2">⟳</span>
                        Saving...
                      </>
                    ) : module.assigned ? (
                      "Enabled ✓"
                    ) : (
                      "Disabled"
                    )}
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-6 text-center">
              <button
                onClick={() => setShowModulesModal(false)}
                className="educator-button px-8 py-3 rounded-lg text-white font-semibold"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Due Dates Modal */}
      {showDueDatesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowDueDatesModal(false)}>
          <div className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Set Session Due Dates</h2>
              <button
                onClick={() => setShowDueDatesModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
                data-testid="close-due-dates-modal"
              >
                ×
              </button>
            </div>

            <p className="text-gray-600 mb-6">
              Set informational due dates for each session. Students will see a &quot;Past Due&quot; indicator if the date has passed, but they can still complete the work.
            </p>

            <div className="space-y-4">
              {[1, 2, 3, 4, 5, 6, 7].map((sessionNum) => {
                const currentDueDate = getDueDateForSession(sessionNum);
                const isPast = isPastDue(sessionNum);
                return (
                  <div
                    key={sessionNum}
                    className={`flex items-center justify-between p-4 border-2 rounded-lg ${isPast ? 'border-red-200 bg-red-50' : 'border-gray-200'}`}
                    data-testid={`due-date-session-${sessionNum}`}
                  >
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-800">Session {sessionNum}</h3>
                      {currentDueDate && (
                        <p className={`text-sm ${isPast ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                          Due: {currentDueDate.toLocaleDateString()} {isPast && '(Past Due)'}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="date"
                        defaultValue={currentDueDate ? currentDueDate.toISOString().split('T')[0] : ''}
                        onChange={(e) => handleSetDueDate(sessionNum, e.target.value)}
                        disabled={savingDueDate === sessionNum}
                        className="px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-orange-400"
                        data-testid={`due-date-input-${sessionNum}`}
                      />
                      {currentDueDate && (
                        <button
                          onClick={() => handleRemoveDueDate(sessionNum)}
                          disabled={savingDueDate === sessionNum}
                          className="text-red-600 hover:text-red-800 text-sm font-semibold disabled:opacity-50"
                          data-testid={`remove-due-date-${sessionNum}`}
                        >
                          Remove
                        </button>
                      )}
                      {savingDueDate === sessionNum && (
                        <span className="text-gray-500 text-sm">Saving...</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 text-center">
              <button
                onClick={() => setShowDueDatesModal(false)}
                className="educator-button px-8 py-3 rounded-lg text-white font-semibold"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Announcement Modal */}
      {showAnnouncementModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowAnnouncementModal(false)}>
          <div className="bg-white rounded-2xl p-8 max-w-xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                {editingAnnouncement ? 'Edit Announcement' : 'New Announcement'}
              </h2>
              <button
                onClick={() => setShowAnnouncementModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
                data-testid="close-announcement-modal"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCreateAnnouncement}>
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Title</label>
                <input
                  type="text"
                  value={announcementForm.title}
                  onChange={(e) => setAnnouncementForm({ ...announcementForm, title: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-400"
                  placeholder="Announcement title..."
                  required
                  data-testid="announcement-title-input"
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Message</label>
                <textarea
                  value={announcementForm.content}
                  onChange={(e) => setAnnouncementForm({ ...announcementForm, content: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-400 min-h-[120px]"
                  placeholder="Write your announcement..."
                  required
                  data-testid="announcement-content-input"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowAnnouncementModal(false)}
                  className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingAnnouncement}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 px-4 py-3 rounded-lg text-white font-semibold disabled:opacity-50"
                  data-testid="submit-announcement-btn"
                >
                  {savingAnnouncement ? 'Posting...' : (editingAnnouncement ? 'Update' : 'Post Announcement')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Student Answers Modal */}
      {showAnswersModal && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowAnswersModal(false)}>
          <div className="bg-white rounded-2xl p-8 max-w-4xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800">{selectedStudent.name}&apos;s Answers</h2>
              <button
                onClick={() => setShowAnswersModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
                data-testid="close-answers-modal"
              >
                ×
              </button>
            </div>

            {studentAnswers.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📝</div>
                <p className="text-gray-600">No answers submitted yet</p>
              </div>
            ) : (
              <div className="space-y-6">
                {studentAnswers.map((answer, idx) => (
                  <div key={answer.id} className="border-2 border-gray-200 rounded-lg p-6">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-bold text-gray-800">Answer #{idx + 1}</h3>
                      <span className="text-sm text-gray-500">
                        {new Date(answer.submitted_at).toLocaleString()}
                      </span>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-700 whitespace-pre-wrap">{answer.answer_text}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ClassView;
